import CountPip from '@orn/shared-web/components/CountPip';
import { BellIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import isPlainLeftClick from '@orn/shared-web/utils/isPlainLeftClick';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';

interface INotificationBellButtonProps {
  /**
   * Unread alert count. The pip is hidden at zero and clamps its display at
   * "9+"; the accessible name always carries the true number.
   */
  unreadCount: number;
  /** Whether the Alerts Center is the surface currently on screen. */
  current?: boolean;
  /**
   * Opens the center in the side panel instead of navigating. Supplied only at
   * `xl+`, where the panel exists; a plain left-click takes this path and every
   * modified click still follows the href.
   */
  onOpenInPanel?: () => void;
}

/**
 * Bell entry-point to the Alerts Center, with an unread-count pip overlaying
 * the icon (Figma node `6383:472847`). Presentational — the caller
 * (`MainSidebar`) resolves the `feat-alerts-center` gate, supplies the count,
 * and decides whether the panel is available; this component renders
 * unconditionally when mounted.
 *
 * A link rather than a button, so the destination supports middle-click and
 * open-in-new-tab like every other nav destination in this chrome. At `xl+` the
 * center is a side panel rather than a route, so a plain left-click opens it in
 * place via `onOpenInPanel` — going through the route there would redirect the
 * content column to the dashboard and cost the user the pane they were reading.
 * Modified clicks skip the handler and open `/alerts` in a new tab, where the
 * route gate hands the panel its content.
 *
 * `unreadCount` is the `unread` bucket of the work-item status counts the
 * center's own reads use, so the badge and the center cannot disagree. It
 * counts unread only — the center's "Need attention" header covers unread
 * *and* read, so the two numbers are deliberately different. Every alert the
 * center lists is a stored row, phone verification included, so nothing it
 * shows is missing from the count.
 *
 * Native `title` over the `Tooltip` primitive deliberately: `Tooltip`'s
 * react-aria trigger passes its props through context that a `RouterLink`
 * does not consume, so the tooltip would never open. The broader migration
 * off `title` is tracked in ORN-657.
 */
const NotificationBellButton = ({ unreadCount, current, onOpenInPanel }: INotificationBellButtonProps) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onOpenInPanel || !isPlainLeftClick(event)) {
      return;
    }

    event.preventDefault();
    onOpenInPanel();
  };

  return (
    <RouterLink
      to={PATHS.ALERTS}
      onClick={handleClick}
      title="Alerts"
      aria-label={unreadCount > 0 ? `Alerts, ${unreadCount} unread` : 'Alerts'}
      aria-current={current ? 'page' : undefined}
      className={cx(
        'outline-focus-ring relative flex size-9 shrink-0 items-center justify-center rounded-full transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2',
        current
          ? 'bg-active text-fg-quaternary_hover'
          : 'text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover'
      )}
    >
      <BellIcon aria-hidden="true" className="size-5 shrink-0" />
      <CountPip count={unreadCount} />
    </RouterLink>
  );
};

export default NotificationBellButton;
